import React from "react";
import { css } from "@linaria/core";
import type { DateValue } from "@internationalized/date";
import { getLocalTimeZone, parseAbsolute, toCalendarDate } from "@internationalized/date";
import { DatePicker } from "@ui/DatePicker";
import ModalForm from "@ui/Modal/Form";
import { spacings } from "@ui/Theme";
import Text from "@ui/Text";
import useToasts from "@ui/Toast/useToasts";
import useInput from "@ui/lib/hooks/useInput";
import { usePost } from "../../api/useCall";
import type { Source } from "../../api/useSources";
import { useSources } from "../../api/useSources";
import t from "../../i18n";
import InputV2 from "@ui/Input/InputV2";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";

interface P {
  source: Source;
  onRequestClose: () => void;
}

const fieldsRow = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacings.s};
`;

const helperText = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
`;

const toLocalBoundaryDate = (value: DateValue | null, boundary: "start" | "end") => {
  if (value === null) return null;

  const date = toCalendarDate(value).toDate(getLocalTimeZone());
  if (boundary === "start") {
    date.setHours(0, 0, 0, 0);
  } else {
    date.setHours(23, 59, 59, 999);
  }

  return date;
};

const DuplicateModalForm = ({ source, onRequestClose }: P) => {
  const sources = useSources();
  const { addToast } = useToasts();

  const newSourceId = useInput(t("sources.duplicate.duplicated_name", { name: source.id }));
  const [from, setFrom] = React.useState<DateValue | null>(null);
  const [to, setTo] = React.useState<DateValue | null>(null);
  const duplicateSource = usePost("/sources/:sourceId/duplicate");

  const inputNameError = React.useMemo(() => {
    if (newSourceId.value === "") return t("sources.duplicate.input.name.error.cannot_be_empty");

    if (isLeft(short(io.string).decode(newSourceId.value)))
      return t("sources.duplicate.input.name.error.too_long");

    if (sources.some(s => s.id === newSourceId.value.trim()))
      return t("sources.duplicate.input.name.error.name_already_exists");

    return;
  }, [sources, newSourceId.value]);

  const parsedFrom = React.useMemo(() => toLocalBoundaryDate(from, "start"), [from]);
  const parsedTo = React.useMemo(() => toLocalBoundaryDate(to, "end"), [to]);

  const sourceMinDateValue = React.useMemo(
    () =>
      source.minSnapshotDate
        ? toCalendarDate(parseAbsolute(source.minSnapshotDate, getLocalTimeZone()))
        : undefined,
    [source.minSnapshotDate],
  );
  const sourceMaxDateValue = React.useMemo(
    () =>
      source.maxSnapshotDate
        ? toCalendarDate(parseAbsolute(source.maxSnapshotDate, getLocalTimeZone()))
        : undefined,
    [source.maxSnapshotDate],
  );

  const dateRangeError =
    parsedFrom instanceof Date && parsedTo instanceof Date && parsedFrom > parsedTo
      ? t("sources.duplicate.input.date.error.invalid_range")
      : undefined;

  const handleSubmit = React.useCallback(() => {
    if (inputNameError || dateRangeError) return false;
    const closeInfo = addToast(
      "info",
      t("sources.duplicate.toast.info", {
        originSourceId: source.id,
        targetSourceId: newSourceId.value,
      }),
    );
    void duplicateSource({
      routeParams: { sourceId: encodeURIComponent(source.id) },
      body: {
        targetSourceId: newSourceId.value,
        from: parsedFrom instanceof Date ? parsedFrom : null,
        to: parsedTo instanceof Date ? parsedTo : null,
      },
    }).then(() => {
      closeInfo();
      addToast(
        "success",
        t("sources.duplicate.toast.success", {
          originSourceId: source.id,
          targetSourceId: newSourceId.value,
        }),
      );
    });

    return true;
  }, [
    addToast,
    dateRangeError,
    duplicateSource,
    inputNameError,
    newSourceId.value,
    parsedFrom,
    parsedTo,
    source.id,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={inputNameError || dateRangeError ? false : () => handleSubmit()}
      okButtonName={t("modal.duplicate")}
    >
      <div>{t("sources.duplicate.warning", { name: source.id })}</div>
      <InputV2
        label={t("sources.duplicate.label")}
        required
        error={inputNameError}
        placeholder={t("sources.delete.placeholder")}
        {...newSourceId}
        revertable={false}
      />
      <div className={helperText}>
        <Text type="body2">{t("sources.duplicate.range.helper")}</Text>
        <Text type="body2">{t("sources.duplicate.range.helper_bounds")}</Text>
      </div>
      <div className={fieldsRow}>
        <DatePicker
          id="duplicate-source-range-from"
          label={t("sources.duplicate.range.from")}
          error={dateRangeError}
          dateOptions={{
            granularity: "day",
            value: from,
            onChange: setFrom,
            minValue: sourceMinDateValue,
            maxValue: sourceMaxDateValue,
          }}
        />
        <DatePicker
          id="duplicate-source-range-to"
          label={t("sources.duplicate.range.to")}
          error={dateRangeError}
          dateOptions={{
            granularity: "day",
            value: to,
            onChange: setTo,
            minValue: sourceMinDateValue,
            maxValue: sourceMaxDateValue,
          }}
        />
      </div>
    </ModalForm>
  );
};

export default DuplicateModalForm;
